/**
 * Assignment Form Component
 *
 * Modal form for creating new SLA tier assignments for clients.
 * Handles tier selection and automatic expiration of previous assignments.
 *
 * @module components/SLA/AssignmentForm
 */

import React, { useState, useEffect } from 'react';
import { SLATier } from '../../../../shared/types/sla';
import slaService from '../../services/slaService';
import { Modal, Button } from '../ui';

interface Client {
  client_id: string;
  client_name: string;
}

interface AssignmentFormProps {
  /** Pre-selected client ID (for changing existing assignment) */
  preselectedClientId?: string;
  /** Callback when assignment is successfully created */
  onAssignmentCreated: () => void;
  /** Callback to close the modal */
  onClose: () => void;
}

/**
 * Assignment Form - Create new SLA tier assignment
 */
const AssignmentForm: React.FC<AssignmentFormProps> = ({
  preselectedClientId,
  onAssignmentCreated,
  onClose,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [tiers, setTiers] = useState<SLATier[]>([]);
  const [selectedClientId, setSelectedClientId] = useState(preselectedClientId || '');
  const [selectedTierId, setSelectedTierId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoadingData(true);

      // Load tiers (exclude deprecated)
      const allTiers = await slaService.getTiers({ include_deprecated: false });
      setTiers(allTiers);

      // Load clients (mock - replace with actual client API)
      // For now, we'll use a placeholder
      setClients([
        { client_id: 'client-1', client_name: 'Client A' },
        { client_id: 'client-2', client_name: 'Client B' },
      ]);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedClientId || !selectedTierId) {
      setError('Please select both client and tier');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await slaService.createAssignment({
        client_id: selectedClientId,
        sla_tier_id: selectedTierId,
      });

      onAssignmentCreated();
    } catch (err) {
      console.error('Failed to create assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to create assignment');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(
    (c) =>
      c.client_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      c.client_id.toLowerCase().includes(clientSearch.toLowerCase())
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      size='sm'
      title={preselectedClientId ? 'Change SLA Tier Assignment' : 'New SLA Tier Assignment'}
      footer={
        <>
          <Button variant='outline' type='button' onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type='submit'
            form='sla-assignment-form'
            disabled={loading || loadingData || !selectedClientId || !selectedTierId}
          >
            {loading ? 'Assigning...' : 'Assign Tier'}
          </Button>
        </>
      }
    >
      <p className='text-sm text-[var(--color-text-secondary)]'>
        Assign an SLA tier to a client. Previous assignment will be automatically expired.
      </p>

      {error && (
        <div className='mt-4 rounded-md bg-[var(--color-status-danger)]/10 p-4'>
          <div className='text-sm text-[var(--color-status-danger)]'>{error}</div>
        </div>
      )}

      {loadingData ? (
        <div className='mt-4 text-center text-[var(--color-text-secondary)]'>Loading...</div>
      ) : (
        <form id='sla-assignment-form' onSubmit={handleSubmit} className='mt-6 space-y-4'>
          {/* Client Selection */}
          <div>
            <label htmlFor='client' className='block text-sm font-medium text-[var(--color-text-primary)]'>
              Client *
            </label>
            {preselectedClientId ? (
              <div className='mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] py-2 px-3 text-sm text-[var(--color-text-primary)]'>
                {clients.find((c) => c.client_id === preselectedClientId)?.client_name ||
                  preselectedClientId}
              </div>
            ) : (
              <>
                <input
                  type='text'
                  placeholder='Search clients...'
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className='mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
                />
                <select
                  id='client'
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  required
                  className='mt-2 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
                >
                  <option value=''>Select a client</option>
                  {filteredClients.map((client) => (
                    <option key={client.client_id} value={client.client_id}>
                      {client.client_name}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          {/* Tier Selection */}
          <div>
            <label htmlFor='tier' className='block text-sm font-medium text-[var(--color-text-primary)]'>
              SLA Tier *
            </label>
            <select
              id='tier'
              value={selectedTierId}
              onChange={(e) => setSelectedTierId(e.target.value)}
              required
              className='mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
            >
              <option value=''>Select a tier</option>
              {tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.tier_name} - {tier.uptime_percent}% uptime
                </option>
              ))}
            </select>
            {selectedTierId && (
              <div className='mt-2 text-xs text-[var(--color-text-secondary)] space-y-1'>
                {tiers.find((t) => t.id === selectedTierId) && (
                  <>
                    <div>
                      Uptime: {tiers.find((t) => t.id === selectedTierId)!.uptime_percent}%
                    </div>
                    <div>
                      RTO: {tiers.find((t) => t.id === selectedTierId)!.rto_hours} hours
                    </div>
                    <div>
                      RPO: {tiers.find((t) => t.id === selectedTierId)!.rpo_hours} hours
                    </div>
                    <div>
                      Penalty: $
                      {tiers.find((t) => t.id === selectedTierId)!.penalty_per_hour_usd}/hour
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
};

export default AssignmentForm;
export { AssignmentForm };
